import { useCallback, useMemo } from "react";

import type { BillingStatus, TicketListAvailable_BillingPageQuery } from "@/lib/gql/__generated__/graphql";
import { CurrencyType } from "@/lib/gql/__generated__/graphql";

import type {
	BillingSettingDialogStateType,
	BillingSettingDialogType,
	OnUpdateRecurringPurchaseProBillingTicketType,
} from "@/components/features/settings/billing/model/use-case";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { RecurringPurchaseDialogContent } from "./components/recurring-purchase-dialog-content";
import { RecurringTicketInfo } from "./components/recurring-ticket-info";
import { useRecurringTicketState } from "./hooks/use-recurring-ticket-state";
import { PurchaseTicketDialog } from "../../../../_shared/dialog";
import { SettingsBillingSection } from "../../../../_shared/section";
import { useTicketList } from "../../../../_shared/ticket-list/ticket-list";

import type { TicketListItemType } from "../../../../_shared/ticket-list/ticket-list";

type ProTicketRecurringPurchaseTicketSectionProps = {
	ticketProListAvailableContinuousBilling: TicketListAvailable_BillingPageQuery["ticketProListAvailableContinuousBilling"];
	billingStatus: BillingStatus;
	onRecurringPurchaseProBillingTicket: OnUpdateRecurringPurchaseProBillingTicketType;
	openBillingSettingDialog: BillingSettingDialogStateType;
	handleOpenChange: (type: BillingSettingDialogType) => (callBackFunc: NodeJS.Timeout) => (open: boolean) => void;
	activateBillingSettingDialog: (type: BillingSettingDialogType) => void;
	deactivateBillingSettingDialog: (type: BillingSettingDialogType) => void;
};

export const ProTicketRecurringPurchaseTicketSection = ({
	ticketProListAvailableContinuousBilling,
	billingStatus,
	onRecurringPurchaseProBillingTicket,
	openBillingSettingDialog,
	handleOpenChange: _handleOpenChange,
	activateBillingSettingDialog,
	deactivateBillingSettingDialog,
}: ProTicketRecurringPurchaseTicketSectionProps) => {
	const {
		defaultRecurringProBillingTicketID,
		canShowNextBillingDtPro,
		isStopRecurringProBilling,
		onUpdateIsStopRecurringProBilling,
		onResetIsStopRecurringProBilling,
	} = useRecurringTicketState({ billingStatus });

	// チケットのrender hooks を初期化（チケットのリストコンポーネントと選択されているチケットIDを返す）
	const { selectedTicketID, ticketList_Component, handleChangeSelectedTicketID } = useTicketList({
		accentColor: "yellow",
		defaultTicketID: defaultRecurringProBillingTicketID,
		ticketList: ticketProListAvailableContinuousBilling.tickets.map((ticket) => {
			return {
				id: ticket?.hotelProBillingTicketID ?? "",
				name: ticket?.name || "",
				validMonth: ticket?.validMonth || 0,
				price: ticket?.price || { amount: 0, currencyType: CurrencyType.Jpy },
			} satisfies TicketListItemType;
		}),
	});

	const handleOpenChange = useCallback(
		(open: boolean) =>
			_handleOpenChange("RECURRING_PRO")(
				setTimeout(() => {
					handleChangeSelectedTicketID(defaultRecurringProBillingTicketID ?? "");
					onResetIsStopRecurringProBilling();
				}, 100),
			)(open),
		[
			_handleOpenChange,
			defaultRecurringProBillingTicketID,
			handleChangeSelectedTicketID,
			onResetIsStopRecurringProBilling,
		],
	);

	const handleClick = useCallback(() => {
		activateBillingSettingDialog("RECURRING_PRO");
	}, [activateBillingSettingDialog]);

	const isValid = useMemo(() => !!selectedTicketID, [selectedTicketID]);

	const handleUpdate = useCallback(async () => {
		if (!selectedTicketID) {
			return;
		}

		deactivateBillingSettingDialog("RECURRING_PRO");

		const result = await onRecurringPurchaseProBillingTicket({
			defaultProBillingTicketID: selectedTicketID,
			isStopRecurringProBilling,
		});

		if (result) {
			notifyUserEventInfo({
				title: "定額チケットの定期購入設定を更新しました",
			});
		}
	}, [
		deactivateBillingSettingDialog,
		onRecurringPurchaseProBillingTicket,
		selectedTicketID,
		isStopRecurringProBilling,
	]);

	return (
		<SettingsBillingSection title="定期購入中のチケット">
			<RecurringTicketInfo
				billingStatus={billingStatus}
				canShowNextBillingDtPro={canShowNextBillingDtPro}
				onChangeSettingClick={handleClick}
			/>

			<PurchaseTicketDialog
				title="予約手数料 | 定額チケットの定期購入設定"
				open={openBillingSettingDialog.RECURRING_PRO}
				handleOpenChange={handleOpenChange}
			>
				<RecurringPurchaseDialogContent
					ticketListComponent={ticketList_Component}
					isStopRecurringProBilling={isStopRecurringProBilling}
					onUpdateIsStopRecurringProBilling={onUpdateIsStopRecurringProBilling}
					isValid={isValid}
					onUpdate={handleUpdate}
				/>
			</PurchaseTicketDialog>
		</SettingsBillingSection>
	);
};

ProTicketRecurringPurchaseTicketSection.displayName = "ProTicketRecurringPurchaseTicketSection";
